import React, { useEffect, useRef, useState } from 'react';

/**
 * Calm, editorial progress overlay for the PDF export.
 *
 * The PDF endpoint is synchronous (no real percentage stream from the
 * server), so this overlay drives **deterministic, eased** progress
 * toward a 95% ceiling and then snaps to 100% when the parent flips
 * `state` to `done`. That gives clients an honest "still working, not
 * frozen" signal without ever falsifying real completion.
 *
 * Stages (time-budgeted for a typical large-board export ~50s):
 *   • 0%  →  20%  preparing images   (~6s)
 *   • 20% →  70%  building PDF       (~28s — longest stage)
 *   • 70% →  90%  finalizing file    (~8s)
 *   • 90% →  95%  hold (waiting on response)
 *   • response received → snap to 100% + "Download starting"
 *
 * If 20s passes and we're still waiting, a soft hint appears under the
 * progress bar — never an alarm, never a spinner — explaining that
 * large boards can take a moment.
 *
 * Props:
 *   • state — 'idle' | 'running' | 'done' | 'failed'
 *   • t — translation map (calls t.export_stage_*, etc.)
 *   • onDismiss — optional close handler for terminal states only.
 */

const STAGES = [
  { key: 'preparing',  cap: 0.20, ms: 6000  },
  { key: 'building',   cap: 0.70, ms: 28000 },
  { key: 'finalizing', cap: 0.90, ms: 8000  },
  { key: 'hold',       cap: 0.95, ms: 30000 },
];

const ExportProgressOverlay = ({ state = 'idle', t = {}, onDismiss }) => {
  const [progress, setProgress] = useState(0);
  const [stageKey, setStageKey] = useState('preparing');
  const [showLongHint, setShowLongHint] = useState(false);
  const startedAtRef = useRef(null);
  const rafRef = useRef(null);

  // Smoothly tween toward each stage's cap. Pure CSS transitions would
  // give us the visual smoothness for free, but driving the value here
  // also lets us advance `stageKey` at the right boundary so the label
  // and the bar move in sync.
  useEffect(() => {
    if (state !== 'running') return;
    startedAtRef.current = performance.now();
    setProgress(0);
    setStageKey('preparing');
    setShowLongHint(false);

    const tick = () => {
      const elapsed = performance.now() - startedAtRef.current;
      let acc = 0;
      let pct = 0;
      let activeStage = STAGES[STAGES.length - 1].key;
      for (const stg of STAGES) {
        if (elapsed < acc + stg.ms) {
          const stageElapsed = elapsed - acc;
          const stagePct = Math.min(1, stageElapsed / stg.ms);
          // ease-out cubic so the bar slows near the cap (feels honest:
          // we're _approaching_ done, not hammering toward it).
          const eased = 1 - Math.pow(1 - stagePct, 3);
          const prevCap = STAGES.indexOf(stg) === 0 ? 0 : STAGES[STAGES.indexOf(stg) - 1].cap;
          pct = prevCap + (stg.cap - prevCap) * eased;
          activeStage = stg.key;
          break;
        }
        acc += stg.ms;
        pct = stg.cap;
      }
      setProgress(pct);
      setStageKey(activeStage);
      if (elapsed >= 20000 && !showLongHint) setShowLongHint(true);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [state, showLongHint]);

  // Snap to 100% + "Download starting" the moment the response lands.
  useEffect(() => {
    if (state === 'done') {
      cancelAnimationFrame(rafRef.current);
      setProgress(1);
      setStageKey('download');
    }
  }, [state]);

  if (state === 'idle') return null;

  const STAGE_LABELS = {
    preparing:  t.export_stage_preparing  || 'Preparing images',
    building:   t.export_stage_building   || 'Building PDF',
    finalizing: t.export_stage_finalizing || 'Finalizing file',
    hold:       t.export_stage_finalizing || 'Finalizing file',
    download:   t.export_stage_download   || 'Download starting',
  };

  const isTerminal = state === 'done' || state === 'failed';
  const failed = state === 'failed';

  return (
    <>
      <div
        // Calm dimmer — slightly lighter than the modal backdrop so it
        // reads as a quiet wait state, not an alert.
        data-testid="export-progress-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(245, 243, 240, 0.72)',
          backdropFilter: 'saturate(140%) blur(2px)',
          WebkitBackdropFilter: 'saturate(140%) blur(2px)',
          zIndex: 1100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
        }}
      >
        <div
          data-testid="export-progress-panel"
          role="status"
          aria-live="polite"
          style={{
            background: 'var(--kahla-cream)',
            border: '1px solid var(--kahla-border)',
            padding: '32px 36px',
            width: '100%',
            maxWidth: '420px',
            textAlign: 'center',
            boxShadow: '0 12px 40px rgba(0,0,0,0.06)',
          }}
        >
          <p
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: '12px',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: 'var(--kahla-text-light)',
              marginBottom: '16px',
              fontWeight: 500,
            }}
          >
            {failed ? (t.toast_export_failed || 'Export failed') : (t.export_to_pdf_title || 'Export to PDF')}
          </p>

          <p
            data-testid="export-progress-stage"
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: '22px',
              color: 'var(--kahla-charcoal)',
              marginBottom: '24px',
              fontWeight: 500,
              lineHeight: 1.3,
            }}
          >
            {failed ? (t.export_failed_calm || 'The export could not be completed. Please try again in a moment.') : STAGE_LABELS[stageKey]}
          </p>

          {!failed && (
            <>
              {/* Hairline progress track + filled span. CSS transition
                  smooths the discrete tick updates into a continuous
                  motion so the bar reads as a calm slide rather than a
                  jerky stepper. */}
              <div
                aria-hidden="true"
                style={{
                  position: 'relative',
                  height: '2px',
                  background: 'var(--kahla-border)',
                  marginBottom: '12px',
                  overflow: 'hidden',
                }}
              >
                <div
                  data-testid="export-progress-fill"
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${Math.round(progress * 100)}%`,
                    background: 'var(--kahla-charcoal)',
                    transition: 'width 0.6s ease',
                  }}
                />
              </div>
              <p
                data-testid="export-progress-percent"
                style={{
                  fontSize: '11px',
                  letterSpacing: '0.14em',
                  color: 'var(--kahla-text-light)',
                  fontFamily: '"Playfair Display", serif',
                  margin: 0,
                }}
              >
                {Math.round(progress * 100)}%
              </p>
            </>
          )}

          {showLongHint && !isTerminal && (
            <p
              data-testid="export-progress-long-hint"
              style={{
                marginTop: '22px',
                fontSize: '12px',
                fontStyle: 'italic',
                fontFamily: '"Cormorant Garamond", "Playfair Display", serif',
                color: 'var(--kahla-text-light)',
                lineHeight: 1.55,
              }}
            >
              {t.export_long_wait_hint || 'Large boards can take a little longer. Please keep this window open.'}
            </p>
          )}

          {failed && onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              className="btn-secondary"
              data-testid="export-progress-dismiss"
              style={{ marginTop: '8px', padding: '8px 22px', fontSize: '12px', letterSpacing: '0.1em' }}
            >
              {t.close || 'Close'}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default ExportProgressOverlay;
